import { useState, useCallback } from 'react';
import axios from 'axios';
import { API_BASE } from '../../../config/api';
import { ActionItem, EditValues, NewCardValues, STATUS_MAP, TaskStatus } from '../types';
import { convert24Hour, convertTo12Hour } from '../utils';

interface Founder {
  id: string;
  name: string;
}

export function useTasks() {
  const [actionItems, setActionItems] = useState<ActionItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [allFounders, setAllFounders] = useState<Founder[]>([]);
  const [editingCard, setEditingCard] = useState<string | null>(null);
  const [editValues, setEditValues] = useState<EditValues>({
    title: '',
    description: '',
    dueDate: '',
    dueTime: '',
    recurrence: '',
    reminderOffsetMinutes: '15',
  });
  const [creatingCard, setCreatingCard] = useState<string | null>(null);
  const [newCardValues, setNewCardValues] = useState<NewCardValues>({
    title: '',
    description: '',
    dueDate: '',
    dueTime: '',
    recurrence: '',
    status: 'pending',
    reminderOffsetMinutes: '15',
  });

  const loadActionItems = useCallback(async () => {
    try {
      setLoading(true);

      const response = await axios.get(`${API_BASE}/api/tasks`, {
        withCredentials: true,
      });

      if (response.data.success) {
        setActionItems(response.data.tasks || []);
      } else {
        setActionItems([]);
      }

      // Also fetch founders for fallback matching
      try {
        const foundersResponse = await axios.get(`${API_BASE}/api/founders`, {
          withCredentials: true,
        });
        const foundersArray = Array.isArray(foundersResponse.data)
          ? foundersResponse.data
          : foundersResponse.data?.data || [];

        if (Array.isArray(foundersArray)) {
          setAllFounders(
            foundersArray.map((f: any) => ({
              id: f.id,
              name: f.name,
            }))
          );
        }
      } catch (founderError) {
        console.error('Error loading founders:', founderError);
      }
    } catch (error) {
      console.error('Error loading action items:', error);
      setActionItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleStatusChange = useCallback(
    async (actionId: string, newStatus: TaskStatus) => {
      const apiStatus = STATUS_MAP[newStatus];

      // Update local state immediately for smooth UX
      setActionItems((prev) =>
        prev.map((item) => (item.id === actionId ? { ...item, status: apiStatus } : item))
      );

      try {
        await axios.patch(
          `${API_BASE}/api/tasks/${actionId}`,
          { status: apiStatus },
          {
            withCredentials: true,
            headers: { 'Content-Type': 'application/json' },
          }
        );
      } catch (error) {
        console.error('Error updating task status:', error);
        loadActionItems();
      }
    },
    [loadActionItems]
  );

  const getItemsByStatus = useCallback(
    (status: TaskStatus) => {
      const apiStatus = STATUS_MAP[status];
      return actionItems.filter((item) => item.status === apiStatus);
    },
    [actionItems]
  );

  const handleEditClick = useCallback((item: ActionItem) => {
    setEditingCard(item.id);
    setEditValues({
      title: item.title,
      description: item.description || '',
      dueDate: item.dueDate ? new Date(item.dueDate).toISOString().split('T')[0] : '',
      dueTime: item.dueTime ? convertTo12Hour(item.dueTime) : '',
      recurrence: item.recurrence || '',
      reminderOffsetMinutes: (item.reminderOffsetMinutes ?? 15).toString(),
    });
  }, []);

  const hasEdits = useCallback(
    (item: ActionItem) => {
      if (!editingCard || editingCard !== item.id) return false;
      const originalDate = item.dueDate ? new Date(item.dueDate).toISOString().split('T')[0] : '';
      return (
        editValues.title !== item.title ||
        editValues.description !== (item.description || '') ||
        editValues.dueDate !== originalDate ||
        editValues.dueTime !== (item.dueTime || '') ||
        editValues.recurrence !== (item.recurrence || '') ||
        editValues.reminderOffsetMinutes !== String(item.reminderOffsetMinutes ?? 15)
      );
    },
    [editingCard, editValues]
  );

  const handleEditSave = useCallback(
    async (itemId: string) => {
      if (!editValues.title.trim()) {
        alert('Task title is required');
        return;
      }

      // Update local state immediately
      setActionItems((prev) =>
        prev.map((item) =>
          item.id === itemId
            ? {
                ...item,
                title: editValues.title,
                description: editValues.description,
                dueDate: editValues.dueDate || undefined,
                dueTime: editValues.dueTime ? convert24Hour(editValues.dueTime) : undefined,
                recurrence: editValues.recurrence || undefined,
                reminderOffsetMinutes:
                  editValues.reminderOffsetMinutes === ''
                    ? undefined
                    : parseInt(editValues.reminderOffsetMinutes, 10),
              }
            : item
        )
      );

      try {
        await axios.patch(
          `${API_BASE}/api/tasks/${itemId}`,
          {
            title: editValues.title,
            description: editValues.description || null,
            dueDate: editValues.dueDate || null,
            dueTime: editValues.dueTime ? convert24Hour(editValues.dueTime) : null,
            recurrence: editValues.recurrence || null,
            reminderOffsetMinutes:
              editValues.reminderOffsetMinutes === ''
                ? null
                : parseInt(editValues.reminderOffsetMinutes, 10),
          },
          {
            withCredentials: true,
            headers: { 'Content-Type': 'application/json' },
          }
        );
        setEditingCard(null);
      } catch (error) {
        console.error('Error updating task:', error);
        loadActionItems();
      }
    },
    [editValues, loadActionItems]
  );

  const handleEditCancel = useCallback(() => {
    setEditingCard(null);
  }, []);

  const handleCreateClick = useCallback((status: string) => {
    setCreatingCard(status);
    setNewCardValues({
      title: '',
      description: '',
      dueDate: '',
      dueTime: '',
      recurrence: '',
      status: status,
      reminderOffsetMinutes: '15',
    });
  }, []);

  const handleCreateSave = useCallback(async () => {
    if (!newCardValues.title.trim()) {
      alert('Task title is required');
      return;
    }

    try {
      const response = await axios.post(
        `${API_BASE}/api/tasks`,
        {
          title: newCardValues.title,
          description: newCardValues.description,
          status: newCardValues.status,
          dueDate: newCardValues.dueDate ? newCardValues.dueDate.split('T')[0] : null,
          dueTime: newCardValues.dueTime ? convert24Hour(newCardValues.dueTime) : null,
          recurrence: newCardValues.recurrence || null,
          reminderOffsetMinutes:
            newCardValues.reminderOffsetMinutes === ''
              ? null
              : parseInt(newCardValues.reminderOffsetMinutes, 10),
        },
        { withCredentials: true }
      );

      setActionItems((prev) => [...prev, response.data]);
      setCreatingCard(null);
      setNewCardValues({
        title: '',
        description: '',
        dueDate: '',
        dueTime: '',
        recurrence: '',
        status: 'pending',
        reminderOffsetMinutes: '15',
      });
    } catch (error) {
      console.error('Error creating task:', error);
    }
  }, [newCardValues]);

  const handleCreateCancel = useCallback(() => {
    setCreatingCard(null);
    setNewCardValues({
      title: '',
      description: '',
      dueDate: '',
      dueTime: '',
      recurrence: '',
      status: 'pending',
      reminderOffsetMinutes: '15',
    });
  }, []);

  const handleDelete = useCallback(
    async (itemId: string) => {
      if (!window.confirm('Are you sure you want to delete this task?')) {
        return;
      }

      setActionItems((prev) => prev.filter((item) => item.id !== itemId));

      try {
        await axios.delete(`${API_BASE}/api/tasks/${itemId}`, {
          withCredentials: true,
        });
      } catch (error) {
        console.error('Error deleting task:', error);
        loadActionItems();
      }
    },
    [loadActionItems]
  );

  const setNewCardDueDate = useCallback((dueDate: string) => {
    setNewCardValues((prev) => ({ ...prev, dueDate }));
  }, []);

  return {
    actionItems,
    setActionItems,
    loading,
    allFounders,
    editingCard,
    editValues,
    setEditValues,
    creatingCard,
    setCreatingCard,
    newCardValues,
    setNewCardValues,
    loadActionItems,
    handleStatusChange,
    getItemsByStatus,
    handleEditClick,
    hasEdits,
    handleEditSave,
    handleEditCancel,
    handleCreateClick,
    handleCreateSave,
    handleCreateCancel,
    handleDelete,
    setNewCardDueDate,
  };
}
